import { useState, useEffect, useRef } from "react";
import "font-awesome/css/font-awesome.min.css";
import Loader from "./Loader";
import "../../src/App.css";
import "../../src/styles/vipcode.css"
import "../styles/generator.css";
import { getDownPayModalObject, getCompareModalObject, getAssessmentObject, getAuthenticationObject } from "../js/helper";
import Calculations from './Calculations';
import LoanSummaryDetails from './child/LoanSummaryDetails';
import InstallmentTerms from './child/InstallmentTerms';
import CompareScreen from './child/compare/CompareScreen';
import DownPayment from './child/Modal/DownPayment';
import Assessment from './child/Modal/Assessment';
import ScanQr from './child/Modal/ScanQr';
import { ItemsList, FooterButtons } from './child/MainScreenComponents';
import { SidebarLayout } from "./SidebarLayout";
import Authentication from "./child/Modal/Authentication";
import { postQR, getVipLoanStatus, getVipOfferDetails } from "../js/api/vip";
import VipCodeHeader from "./child/Modal/VipCodeHeader";
import { InfoIcon } from "../image/icons";
import { useLeaveConfirm } from "../context/LeaveConfirmContext";

export const VipCode = () => {
  const state = {
    pageSource: "vip",
    merchantStore: JSON.parse(sessionStorage.getItem("merchantStore")),
    RBPObject: JSON.parse(sessionStorage.getItem("RBPObject")),
    promoterDetails: JSON.parse(sessionStorage.getItem("promoterDetails")),
    selectedValue: sessionStorage.getItem("merchantId"),
    userDetails: JSON.parse(sessionStorage.getItem("userDetails")),
  };

  const logic = Calculations(state);
  const [showScanQr, setShowScanQr] = useState(false);
  const { vipCode, setVipCode } = useLeaveConfirm();

  const [authData, setAuthData] = useState(null);
  const [showAuthentication, setShowAuthentication] = useState(false);
  const [attemptCount, setAttemptCount] = useState(1);
  const [offerLoaded, setOfferLoaded] = useState(false);
  const submittedVipCodeRef = useRef("");

  useEffect(() => {
    window.isQrFlowActive = showScanQr || showAuthentication;
    return () => {
      window.isQrFlowActive = false;
    };
  }, [showScanQr, showAuthentication]);

  useEffect(() => {
    setShowScanQr(true);
  }, []);

  useEffect(() => {
    if (vipCode === "") return;
    if (submittedVipCodeRef.current === vipCode) return;
    submittedVipCodeRef.current = vipCode;

    setShowScanQr(false);
    logic.setIsLoading(true);
    setAttemptCount(1);

    postQR({ "vip-code": vipCode, "merchant-id": state.selectedValue })
      .then((response) => {
        logic.setIsLoading(false);
        setAuthData({ ...response.data, attempt: 1 });
        setShowAuthentication(true);
      })
      .catch((err) => {
        logic.setIsLoading(false);
        console.error(err);

        const errorMessage = err?.response?.data?.errors?.[0]?.message || err?.message || "No repayment journey tracked for this vip-code";
        const errorStatus = err?.response?.data?.errors?.[0].errorCode;
        if (errorStatus === "SILREPT03") {
          setAuthData({
            status: "invalidQR",
            message: errorMessage,
            data: {
              message: errorMessage,
              attemptCurrent: 1,
            }
          });
        } else {
          setAuthData({
            status: "errorQr",
            message: errorMessage,
            data: {
              message: errorMessage,
              attemptCurrent: 1,
            }
          });
        }
        setShowAuthentication(true);
      });
  }, [vipCode]);

  const authenticationObject = getAuthenticationObject({
    show: showAuthentication,
    data: authData,
    merchantName: state.promoterDetails.merchantName,
    onPollStatus: () => {
      getVipLoanStatus({ vipCode: vipCode, "merchantId": state.selectedValue, isJourneyCheck: false })
        .then((res) => {
          // res = { "data": { "data": { "status": "00", "message": "Pending" } } }
          const envelope = res.data || {};

          // Unexpected technical error sits at the envelope level: { errors: { code, message } }
          if (envelope.errors) {
            const message = envelope.errors.message || "Currently we are facing technical issues ! Please try again";
            setAuthData({ status: "error", message, data: { message } });
            return;
          }

          // The real journey status is nested under `data`, not on the envelope:
          //   "00" APPROVED | "11" Pending | "12" Expiry | "13" Rejected
          //   "SILREP01" Invalid request | "SILREPT03" QR not found
          const journey = envelope.data || {};
          if (journey.status === "11") return; // still pending → keep polling

          const STATUS_MAP = { "00": "success", "12": "expired", "13": "rejected" };
          const mapped = STATUS_MAP[journey.status] || "errorQr";
          const message = journey.message || "";

          setAuthData((prev) => ({
            ...prev,
            status: mapped,
            message: message,
            attempt: prev?.attempt || attemptCount,
            data: {
              ...(prev?.data || {}),
              message: message,
            }
          }));
        })
        .catch((err) => {
          console.error(err);
          // const message = err?.response?.data?.errors?.message
          //   || err?.response?.data?.message
          //   || err?.message
          //   || "Currently we are facing technical issues ! Please try again";
          // setAuthData({ status: "error", message, data: { message } });
        });
    },
    onReAttempt: () => {
      const threshold = authData?.data?.["re-attempt-threshold"] || 5;
      const nextAttempt = attemptCount + 1;

      // Once the re-attempt threshold is exceeded, show the limit-reached screen
      // and stop — no further postQR call should happen.
      if (nextAttempt > threshold) {
        setAuthData({
          status: "limit_reached",
          attempt: attemptCount,
          data: {
            "re-attempt-threshold": threshold,
            "limit-reached-timeout": authData?.data?.["limit-reached-timeout"],
          },
        });
        return;
      }

      logic.setIsLoading(true);
      setAttemptCount(nextAttempt);

      postQR({ "vip-code": vipCode, "merchant-id": state.selectedValue, "re-attempt": true })
        .then((res) => {
          logic.setIsLoading(false);

          const resData = res.data?.data || res.data || {};
          setAuthData({
            ...resData,
            status: "00",
            attempt: nextAttempt,
            data: {
              ...resData,
              "re-attempt-threshold": threshold,
              "timeout-time": resData["timeout-time"] || authData?.data?.["timeout-time"],
              "polling-time": resData["polling-time"] || authData?.data?.["polling-time"],
            },
          });
        })
        .catch((err) => {
          logic.setIsLoading(false);
          console.error(err);

          const errorMessage = err?.response?.data?.errors?.[0]?.message || err?.message || "No repayment journey tracked for this vip-code";

          setAuthData({
            status: "errorQr",
            message: errorMessage,
            data: {
              message: errorMessage,
              attemptCurrent: nextAttempt,
            }
          });
        });
    },
    onContinue: () => {
      setShowAuthentication(false);
      logic.setIsLoading(true);
      const nextAttempt = attemptCount + 1;
      setAttemptCount(nextAttempt);

      getVipOfferDetails({ "vipCode": vipCode, "merchantId": state.selectedValue })
        .then((response) => {
          logic.setIsLoading(false);
          let info = response.data.data;
          info.segmentCatageries.sort(function (a, b) {
            return a.displayOrder - b.displayOrder;
          });
          const silrepProduct = info.segmentCatageries.find(
            (category) => category.productName === process.env.REACT_APP_SIL_REP
          );
          info.segmentCatageries = info.segmentCatageries.filter(
            (category) => category.productName === process.env.REACT_APP_SIL_REP
          );
          silrepProduct["segmentInfo"].forEach(segment => {
            segment.maxLoanAmount = silrepProduct.maxLoanableAmt;
          });
          info.segmentInfo = silrepProduct["segmentInfo"];

          info.vipCode = vipCode;
          logic.setRBPObject(info);
          setOfferLoaded(silrepProduct);
          logic.setApplicationType(silrepProduct);
          logic.setMaxLoanAmount(silrepProduct.maxLoanableAmt);
        })
        .catch((err) => {
          logic.setIsLoading(false);
          console.error(err);
        });
    },
    onCooldownEnd: () => {
      // Waiting period is over — restart the authentication cycle from attempt 1.
      setAttemptCount(1);
      logic.setIsLoading(true);
      postQR({ "vip-code": vipCode, "merchant-id": state.selectedValue })
        .then((response) => {
          logic.setIsLoading(false);
          setAuthData({ ...response.data, attempt: 1 });
        })
        .catch((err) => {
          logic.setIsLoading(false);
          console.error(err);
          const errorMessage = err?.response?.data?.errors?.[0]?.message || err?.message || "No repayment journey tracked for this vip-code";
          setAuthData({ status: "errorQr", message: errorMessage, data: { message: errorMessage } });
        });
    },
    onScanNew: () => {
      // Reset the entire journey back to the very beginning:
      // close the auth modal, clear all auth state, and reopen the QR scanner.
      setShowAuthentication(false);
      setAuthData(null);
      setVipCode("");
      submittedVipCodeRef.current = "";
      setAttemptCount(1);
      setOfferLoaded(false);
      setShowScanQr(true);
    },
    onClose: () => {
      // X button — treat same as onScanNew: full reset back to QR scan.
      setShowAuthentication(false);
      setAuthData(null);
      setVipCode("");
      submittedVipCodeRef.current = "";
      setAttemptCount(1);
      setOfferLoaded(false);
      setShowScanQr(true);
    }
  });

  return (
    <>
      {authData && <Authentication key={`${authData.status}-${authData.attempt ?? attemptCount}`} {...authenticationObject} />}

      <Assessment {...getAssessmentObject(logic)} />
      <DownPayment {...getDownPayModalObject(logic)} />
      <ScanQr showScanQr={showScanQr} setShowScanQr={setShowScanQr} setVipCode={setVipCode} merchantName={authenticationObject.merchantName} />
      {logic.showCompare && <CompareScreen {...getCompareModalObject(logic)} />}

      <div className="generator-page d-flex">
        <SidebarLayout sideNavPosition={4}>
          {logic.isLoading && <Loader />}
          <VipCodeHeader
            offerLoaded={offerLoaded}
            vipCode={vipCode}
            {...authenticationObject} />
          <div className="page-title">Reverse QR</div>
          <div className={`${!offerLoaded && "overlay-lock"}`} >
            <ItemsList logic={logic} />
          </div>
          {!offerLoaded && <h5 onClick={() => authenticationObject.onScanNew()} className="tonik-color fw-semibold text-center col-md-4 pb-2" style={{ "cursor": "pointer" }}>Scan client’s  QR code</h5>}
          <div className="info-box col-md-4 mb-3">
            <div className="info-box__left">
              {offerLoaded ?
                <span className="info-box__subtitle">The client's Maximum Loanable Amount is {logic.formatter.format(logic.RBPObject.segmentInfo[0].maxLoanAmount)} with a max term of {logic.RBPObject.segmentInfo
                  .flatMap((segment) => segment.tenureInfo)
                  .reduce((highest, current) => (current.tenure > highest.tenure ? current : highest))?.tenure} months</span> :
                <span className="info-box__subtitle">The client's Maximum Loanable Amount is ₱XX,XXX with a max term of X months</span>
              }
            </div>
            <div className="info-box__icon">
              <InfoIcon />
            </div>
          </div>
          <div className={`${!offerLoaded && "overlay-lock"}`}>
            <LoanSummaryDetails {...logic} pageSource="vip"/>
            <InstallmentTerms {...logic} />

            {FooterButtons(
              state,
              logic.assesmentPopupShow,
              logic.disableButton,
              logic.isValidQR,
              logic.applicationType,
              logic.silComProduct,
              logic.selectedCompetitors,
              state.pageSource
            )}
          </div>
        </SidebarLayout>
      </div>
    </>
  );
};

export default VipCode;